import type {
  ListAvailableUGCHashtagsResponse,
  ListMonitoredUGCHashtagsResponse,
} from '@dashhudson/api-client/tiktok/model';
import type dayjs from 'dayjs';

import {
  getGetBrandsBrandIdAccountUrl,
  getGetBrandsBrandIdUgcHashtagsAvailableUrl,
  getGetBrandsBrandIdUgcHashtagsMonitoredUrl,
  getGetBrandsBrandIdUgcTopKeywordsUrl,
  getGetCreatorMarketplaceBrandsBrandIdOrdersCampaignCodeStatsUrl,
  getGetCreatorMarketplaceBrandsBrandIdTtcmAccountsUrl,
  getGetMarketingBrandsBrandIdAdAccountsUrl,
  getPatchCreatorMarketplaceBrandsBrandIdTtcmAccountsSourceTtcmAccountIdUrl,
  getPatchMarketingBrandsBrandIdAdAccountsSourceAdvertiserIdUrl,
  getPostCreatorMarketplaceBrandsBrandIdSparkAdsAuthorizeUrl,
  getPostCreatorMarketplaceBrandsBrandIdSparkAdsUrl,
  getPutCreatorMarketplaceTtcmAccountsUrl,
  getPutMarketingAdAccountsUrl,
  getPutMarketingAdCampaignsUrl,
} from '@dashhudson/api-client/tiktok/client';
import merge from 'lodash/merge';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { env } from '@/env';
import { toUtcQueryDateTime } from '@/utils';

export const http = createDashHttpClient(env.tiktokApiUrl);
export const httpCamelCase = createDashHttpClient(env.tiktokApiUrl, {
  camelizeKeys: true,
});

type BrandArg = { brandId: number };
type DateBound = dayjs.ConfigType;

export function getTikTokAccount({ brandId }: BrandArg, config: HttpRequestConfig = {}) {
  return httpCamelCase.get(getGetBrandsBrandIdAccountUrl(brandId), config);
}

export function getTrendingVideoList(
  {
    brandId,
    startDate,
    endDate,
    limit,
  }: BrandArg & { startDate?: DateBound; endDate?: DateBound; limit?: number },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/brands/${brandId}/top_videos`,
    merge(
      {
        params: {
          startDate: toUtcQueryDateTime(startDate),
          endDate: toUtcQueryDateTime(endDate),
          limit,
        },
      },
      config,
    ),
  );
}

export function getFollowersDemographics(
  { brandId, date }: BrandArg & { date?: DateBound },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/brands/${brandId}/demographics`,
    merge(
      {
        params: {
          date: toUtcQueryDateTime(date),
        },
      },
      config,
    ),
  );
}

export function getVideoViews(
  {
    brandId,
    videoId,
    startDate,
    endDate,
    timeScale,
  }: BrandArg & {
    videoId: number | string;
    startDate?: DateBound;
    endDate?: DateBound;
    timeScale?: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/brands/${brandId}/videos/${videoId}/views`,
    merge(
      {
        params: {
          startDate: toUtcQueryDateTime(startDate),
          endDate: toUtcQueryDateTime(endDate),
          timeScale,
        },
      },
      config,
    ),
  );
}

export async function getTopKeywords(
  {
    brandId,
    includeKeywords,
    includeHashtags,
  }: BrandArg & { includeKeywords?: boolean; includeHashtags?: boolean },
  config: HttpRequestConfig = {},
) {
  return http.get(
    getGetBrandsBrandIdUgcTopKeywordsUrl(brandId),
    merge(
      {
        params: {
          includeKeywords,
          includeHashtags,
        },
      },
      config,
    ),
  );
}

export function getAvailableHashtags({ brandId }: BrandArg, config: HttpRequestConfig = {}) {
  return httpCamelCase.get<ListAvailableUGCHashtagsResponse>(
    getGetBrandsBrandIdUgcHashtagsAvailableUrl(brandId),
    config,
  );
}

export function getMonitoredHashtags({ brandId }: BrandArg, config: HttpRequestConfig = {}) {
  return httpCamelCase.get<ListMonitoredUGCHashtagsResponse>(
    getGetBrandsBrandIdUgcHashtagsMonitoredUrl(brandId),
    config,
  );
}

export async function getAdAccounts(
  {
    brandId,
    fetchApi,
    connectTiktokAdsToken,
  }: BrandArg & { fetchApi?: boolean; connectTiktokAdsToken?: string },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.get(
    getGetMarketingBrandsBrandIdAdAccountsUrl(brandId),
    merge(
      {
        params: {
          fetchApi,
          connectTiktokAdsToken,
        },
      },
      config,
    ),
  );
}

export async function updateAdAccount(
  {
    brandId,
    sourceAdvertiserId,
    isMonitored,
    connectTiktokAdsToken,
  }: BrandArg & {
    sourceAdvertiserId: string;
    isMonitored?: boolean;
    connectTiktokAdsToken?: string;
  },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.patch(
    getPatchMarketingBrandsBrandIdAdAccountsSourceAdvertiserIdUrl(brandId, sourceAdvertiserId),
    { isMonitored, connectTiktokAdsToken },
    config,
  );
}

export async function getAdAccountsAllBrands(
  { brandIds }: { brandIds: number[] },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.put(getPutMarketingAdAccountsUrl(), { brand_ids: brandIds }, config);
}

export async function getAdCampaignsAllBrands(
  {
    brandIds,
    sourceAdAccountIds,
    sourceCampaignIds,
  }: {
    brandIds: number[];
    sourceAdAccountIds?: string[];
    sourceCampaignIds?: string[];
  },
  config: HttpRequestConfig = {},
) {
  const data = {
    brand_ids: brandIds,
    source_ad_account_ids: sourceAdAccountIds,
    source_campaign_ids: sourceCampaignIds,
  };
  return httpCamelCase.put(getPutMarketingAdCampaignsUrl(), data, config);
}

export async function getBrandTTCMAccounts(
  { brandId, fetchApi }: BrandArg & { fetchApi?: boolean },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.get(
    getGetCreatorMarketplaceBrandsBrandIdTtcmAccountsUrl(brandId),
    merge(
      {
        params: {
          fetchApi,
        },
      },
      config,
    ),
  );
}

export async function getTTCMAccountsForMultipleBrands(
  { brandIds }: { brandIds: number[] },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.put(
    getPutCreatorMarketplaceTtcmAccountsUrl(),
    { brand_ids: brandIds },
    config,
  );
}

export async function updateCreatorMarketplaceAccount(
  {
    brandId,
    sourceTtcmAccountId,
    isMonitored,
  }: BrandArg & { sourceTtcmAccountId: string; isMonitored?: boolean },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.patch(
    getPatchCreatorMarketplaceBrandsBrandIdTtcmAccountsSourceTtcmAccountIdUrl(
      brandId,
      sourceTtcmAccountId,
    ),
    { isMonitored },
    config,
  );
}

export async function getCreatorByHandle(
  { handle }: { handle: string },
  config: HttpRequestConfig = {},
) {
  const url = `/creator_marketplace/creators?handle_name=${handle}`;
  return httpCamelCase.get(url, config);
}
export async function getTrendingHashtags(
  {
    categoryName,
    countryCode,
    dateRange,
  }: { categoryName?: string; countryCode?: string; dateRange?: string },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.get(
    `hashtags/trending`,
    merge(
      {
        params: {
          categoryName,
          countryCode,
          dateRange,
        },
      },
      config,
    ),
  );
}

export async function getHashtagDetails(
  {
    hashtagId,
    countryCode,
    dateRange,
  }: { hashtagId: number | string; countryCode?: string; dateRange?: string },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.get(
    `hashtags/${hashtagId}/details`,
    merge(
      {
        params: {
          countryCode,
          dateRange,
        },
      },
      config,
    ),
  );
}

export async function getFeedPosts(
  { url, creatorId, limit }: { url?: string | null; creatorId?: number | string; limit?: number },
  config: HttpRequestConfig = {},
) {
  const apiUrl = url ?? `/creator_marketplace/creators/${creatorId}/feed`;
  return httpCamelCase.get(
    apiUrl,
    merge(
      {
        params: {
          limit,
        },
      },
      config,
    ),
  );
}

export async function getCampaignCreatorsStats(
  { brandId, campaignCode }: BrandArg & { campaignCode: string },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.get(
    getGetCreatorMarketplaceBrandsBrandIdOrdersCampaignCodeStatsUrl(brandId, campaignCode),
    config,
  );
}

export async function getSparkAdsStatus(
  { brandId, sourceVideoId }: BrandArg & { sourceVideoId: string },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.get(
    `/creator_marketplace/brands/${brandId}/spark_ads`,
    merge(
      {
        params: {
          sourceVideoId,
        },
      },
      config,
    ),
  );
}

export async function postSyncToAdAccount(
  {
    brandId,
    authCode,
    sourceAdvertiserId,
  }: BrandArg & { authCode: string; sourceAdvertiserId: string },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.post(
    getPostCreatorMarketplaceBrandsBrandIdSparkAdsAuthorizeUrl(brandId),
    { authCode, sourceAdvertiserId },
    config,
  );
}

export async function requestSparkAdsAuthorization(
  {
    brandId,
    sourceVideoId,
    authorizationDays,
  }: BrandArg & { sourceVideoId: string; authorizationDays: number },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.post(
    getPostCreatorMarketplaceBrandsBrandIdSparkAdsUrl(brandId),
    {
      sourceVideoId,
      authorizationDays,
    },
    config,
  );
}

export async function createMediaForVideo(
  {
    brandId,
    videoId,
    handleName,
  }: { brandId: number | string; videoId: number | string; handleName?: string },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.patch(
    `/creator_marketplace/brands/${brandId}/videos/${videoId}`,
    {},
    merge(
      {
        params: {
          handleName,
        },
      },
      config,
    ),
  );
}

export async function getCreatorMarketplaceCategories(
  { brandId, labelType = 'SEARCH' }: BrandArg & { labelType?: 'RANKING' | 'SEARCH' },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.get(
    `/creator_marketplace/brands/${brandId}/categories`,
    merge(
      {
        params: {
          labelType,
        },
      },
      config,
    ),
  );
}

export function getTrendingSounds(
  {
    brandId,
    dateRange,
    countryCode,
    genre,
  }: { brandId?: number; dateRange?: string; countryCode?: string; genre?: string },
  config: HttpRequestConfig = {},
) {
  return httpCamelCase.get(
    `/trending_sounds`,
    merge(
      {
        params: {
          brandId,
          dateRange,
          countryCode,
          genre,
        },
      },
      config,
    ),
  );
}
